import type { UserRole } from '../enums/user.ts'
import { userRole } from '../enums/user.ts'

/**
 * The role hierarchy as executable rules rather than as a comment on an enum.
 *
 * `employee < admin < superadmin` is an ordering, and an ordering that exists only in
 * prose gets re-derived — differently — at each call site. These four functions are the
 * whole of it; services in `apps/api` compare roles through them and never by hand.
 *
 * Platform admins are deliberately absent. They are a separate population with no tenant
 * and no `UserRole` at all (`docs/DECISIONS.md`, "Platform-admin identity"), so they are
 * not "superadmin plus one" and must not be smuggled into this ordering.
 */

/** Rank of each role: higher is more authority. Derived from the enum's declaration order. */
export const USER_ROLE_RANK: Readonly<Record<UserRole, number>> = Object.freeze(
  Object.fromEntries(userRole.values.map((role, index) => [role, index])) as Record<
    UserRole,
    number
  >,
)

/** Position of `role` in the hierarchy; higher is more authority. */
export function roleRank(role: UserRole): number {
  return USER_ROLE_RANK[role]
}

/**
 * Whether `actual` satisfies a minimum-role requirement.
 *
 * This is the guard for "an endpoint requires admin": a superadmin passes an admin
 * requirement, an employee does not.
 */
export function hasAtLeastRole(actual: UserRole, required: UserRole): boolean {
  return roleRank(actual) >= roleRank(required)
}

/**
 * Every role up to and including `role`, in ascending order.
 *
 * Parity with the current system's `User.allowed_scopes`
 * (`docs/reference/backend/data-model.md` §3), which derives the same list from the
 * enum's declaration order.
 */
export function rolesAtOrBelow(role: UserRole): readonly UserRole[] {
  return userRole.values.filter((candidate) => roleRank(candidate) <= roleRank(role))
}

/**
 * Whether an actor may administer a target **of that role** — create, edit, disable,
 * delete, or reset the password of.
 *
 * Strictly greater, not greater-or-equal, and that is the load-bearing part:
 *
 * - `docs/TESTING.md` §3 requires a test proving an admin cannot act against a
 *   superadmin. Greater-or-equal would let one admin take over another's account, which
 *   is close to the current system's worst authorization hole (any admin can take over
 *   any account — `docs/reference/ISSUES.md`).
 * - `docs/DECISIONS.md` ("Email — none") fixes the recovery chain as *platform admin
 *   resets tenant superadmins; tenant superadmins and admins reset their own users*. A
 *   superadmin resetting another superadmin is not part of that chain, and allowing it
 *   would make a compromised superadmin account able to lock out every peer.
 *
 * A caller that legitimately needs "may act on themselves" — changing your own password —
 * checks identity, not role, and must not reach for this function.
 */
export function canManageRole(actor: UserRole, target: UserRole): boolean {
  return roleRank(actor) > roleRank(target)
}
